import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import SettingsIcon from "@mui/icons-material/Settings";

import { api } from "@api/api";
import {
  coresTextosGlobais,
  ajustarTamanhoFonteEscola,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../../estilos/global";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

const ITEMS_PER_PAGE = 6;

const formatSeiNumber = (seiNumber) => {
  if (!seiNumber) return "";

  const cleaned = String(seiNumber).replace(/\D/g, "");
  let formatted = "";

  if (cleaned.length > 0) formatted += cleaned.substring(0, 4);
  if (cleaned.length > 4) formatted += "." + cleaned.substring(4, 6);
  if (cleaned.length > 6) formatted += "." + cleaned.substring(6, 13);
  if (cleaned.length > 13) formatted += "/" + cleaned.substring(13, 17);
  if (cleaned.length > 17) formatted += "-" + cleaned.substring(17, 19);

  return formatted;
};

const INDICADORES_DOCUMENTOS = [
  {
    campo: "totalDocumentos",
    rotulo: "Total",
    Icone: DescriptionOutlinedIcon,
    cor: "var(--sear-azul, #0767d8)",
  },
  {
    campo: "aprovacao",
    rotulo: "Aprovados",
    situacao: "aprovados",
    Icone: CheckCircleOutlineRoundedIcon,
    cor: "var(--sear-sucesso-texto, #188566)",
  },
  {
    campo: "alteracoes_ativas",
    rotulo: "Em correção",
    situacao: "correcao",
    Icone: EditOutlinedIcon,
    cor: "var(--sear-erro-texto, #c55050)",
  },
  {
    campo: "alteracoes_inativas",
    rotulo: "Em análise",
    situacao: "analise",
    Icone: HourglassTopRoundedIcon,
    cor: "var(--sear-aviso-texto, #b17a0e)",
  },
];

const IndicadoresDocumentos = ({ process, onSelecionar }) => (
  <Box
    role="group"
    aria-label="Situação dos documentos"
    onClick={(event) => event.stopPropagation()}
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      width: "100%",
      minWidth: 0,
      bgcolor: "var(--sear-superficie-azul, #f8fbff)",
      border: "1px solid var(--sear-borda, #e2ebf6)",
      borderRadius: "6px",
      py: "5px",
    }}
  >
    {INDICADORES_DOCUMENTOS.map(
      ({ campo, rotulo, situacao, Icone, cor }, indice) => {
        const quantidade = process[campo] ?? 0;

        return (
          <Box
            key={campo}
            component="span"
            sx={{
              display: "flex",
              minWidth: 0,
              cursor: quantidade > 0 ? "pointer" : "default",
              borderLeft: indice ? "1px solid var(--sear-borda, #e2ebf6)" : 0,
            }}
          >
            <ButtonBase
              type="button"
              disabled={quantidade <= 0}
              aria-label={`${rotulo}: ${quantidade}`}
              onClick={(event) => {
                event.stopPropagation();
                onSelecionar(situacao);
              }}
              sx={{
                display: "block",
                width: "100%",
                minWidth: 0,
                textAlign: "center",
                px: 0.5,
                "&:hover": {
                  bgcolor: "var(--sear-superficie-azul, #edf4fc)",
                },
                "&.Mui-focusVisible": {
                  outline: "2px solid var(--sear-borda-forte, #087cf0)",
                  outlineOffset: "-2px",
                  borderRadius: 0.5,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  color:
                    quantidade > 0 ? cor : "var(--sear-texto-suave, #8b99ab)",
                }}
              >
                <Icone sx={{ fontSize: 15 }} />

                <Typography
                  component="span"
                  sx={{
                    fontSize: ajustarTamanhoFonteEscola("1rem"),
                    fontWeight: 700,
                    lineHeight: 1.125,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {quantidade}
                </Typography>
              </Box>

              <Typography
                component="span"
                sx={{
                  display: "block",
                  color: "var(--sear-texto-secundario, #60738d)",
                  fontSize: tamanhosControlesGlobais.rotulo,
                  lineHeight: 4 / 3,
                  mt: "1px",
                  overflowWrap: "anywhere",
                }}
              >
                {rotulo}
              </Typography>
            </ButtonBase>
          </Box>
        );
      },
    )}
  </Box>
);

const ESTILO_TEXTO_TABELA = {
  fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
  color: coresTextosGlobais.conteudoTabela,
  lineHeight: 1.4,
};

const GRID_PROCESSOS =
  "minmax(220px, 1.25fr) minmax(210px, 1.15fr) 105px minmax(280px, 1.5fr) 112px";

const ESTILO_CELULA = {
  px: 1.4,
  py: 0.6,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  borderRight: "1px solid var(--sear-borda, #e6eff8)",
  height: "100%",
};

const ProcessRow = ({
  process,
  IconeInstituicao,
  onClick,
  onSelecionarSituacao,
}) => (
  <Box
    role="row"
    onClick={onClick}
    sx={{
      display: "grid",
      gridTemplateColumns: GRID_PROCESSOS,
      alignItems: "stretch",
      minHeight: 58,
      borderBottom: "1px solid var(--sear-borda, #e0ebf7)",
      cursor: "pointer",
      "&:hover": {
        bgcolor: "var(--sear-superficie, #fbfdff)",
      },
      "&:last-child": {
        borderBottom: 0,
      },
    }}
  >
    <Box role="cell" sx={ESTILO_CELULA}>
      <Box sx={{ minWidth: 0 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          sx={{
            ...ESTILO_TEXTO_TABELA,
            color: "var(--sear-azul, #0767d8)",
            fontWeight: 600,
            textAlign: "left",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            "&:focus-visible": {
              outline: "2px solid var(--sear-borda-forte, #087cf0)",
              outlineOffset: 2,
            },
          }}
        >
          {process.processtype}
        </Link>

        <Typography
          sx={{
            ...ESTILO_TEXTO_TABELA,
            color: "var(--sear-texto-secundario, #7b8798)",
            fontSize: tamanhosControlesGlobais.rotulo,
            mt: 0.3,
          }}
        >
          SEI: {formatSeiNumber(process.seinumber) || "Não informado"}
        </Typography>
      </Box>
    </Box>

    <Box
      role="cell"
      sx={{
        ...ESTILO_CELULA,
        gap: 0.8,
      }}
    >
      <IconeInstituicao
        sx={{
          color: "var(--sear-azul, #087cf0)",
          fontSize: 18,
          flexShrink: 0,
        }}
      />

      <Typography sx={ESTILO_TEXTO_TABELA}>
        {process.schoolname || "Não informado"}
      </Typography>
    </Box>

    <Box
      role="cell"
      sx={{
        ...ESTILO_CELULA,
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Typography sx={ESTILO_TEXTO_TABELA}>
        {new Date(process.startdate).toLocaleDateString()}
      </Typography>
    </Box>

    <Box role="cell" sx={ESTILO_CELULA}>
      <IndicadoresDocumentos
        process={process}
        onSelecionar={onSelecionarSituacao}
      />
    </Box>

    <Box
      role="cell"
      sx={{
        ...ESTILO_CELULA,
        justifyContent: "center",
        borderRight: 0,
      }}
    >
      <Button
        variant="contained"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        sx={{
          minHeight: 30,
          whiteSpace: "nowrap",
          px: 1.2,
          borderRadius: 1,
          textTransform: "none",
          bgcolor: "var(--sear-acao-azul, #087cf0)",
          fontSize: tamanhosControlesGlobais.botao,
          "&:hover": {
            bgcolor: "var(--sear-acao-azul-hover, #0767d8)",
          },
        }}
      >
        Ver detalhes
      </Button>
    </Box>
  </Box>
);

const ControlesPaginacao = ({
  page,
  totalPages,
  totalProcesses,
  primeiroProcesso,
  ultimoProcesso,
  onChange,
}) => {
  if (totalProcesses === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "var(--sear-texto-secundario, #5f6f94)",
          whiteSpace: "nowrap",
        }}
      >
        Exibindo {primeiroProcesso}–{ultimoProcesso} de {totalProcesses}
      </Typography>

      <Pagination
        count={Math.max(totalPages, 1)}
        page={page}
        onChange={onChange}
        siblingCount={1}
        boundaryCount={1}
        sx={{
          "& .MuiPaginationItem-root": {
            color: "var(--sear-texto, #33446b)",
            borderRadius: 1.5,
          },
          "& .MuiPaginationItem-root.Mui-selected": {
            color: "#ffffff",
            backgroundColor: "var(--sear-acao-azul, #087cf0)",
            "&:hover": {
              backgroundColor: "var(--sear-acao-azul-hover, #0767d8)",
            },
          },
        }}
      />
    </Box>
  );
};

export default function PaginaProcessosEscola() {
  const [abaAtiva, setAbaAtiva] = useState("escola");

  const [buscas, setBuscas] = useState({
    escola: "",
    mantenedora: "",
  });

  const [paginas, setPaginas] = useState({
    escola: 1,
    mantenedora: 1,
  });

  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const listaRef = useRef(null);

  const searchTerm = buscas[abaAtiva];
  const page = paginas[abaAtiva];
  const tipoEscola = abaAtiva === "escola";

  const IconeInstituicao = tipoEscola
    ? SchoolRoundedIcon
    : AccountBalanceRoundedIcon;

  const tituloInstituicao = tipoEscola ? "Escola" : "Mantenedora";

  const quantidades = {
    escola: processes.filter((process) => process.tipoInstituicao === "escola")
      .length,
    mantenedora: processes.filter(
      (process) => process.tipoInstituicao === "mantenedora",
    ).length,
  };

  const colunasProcessos = [
    ["Processo", FolderCopyIcon],
    [tituloInstituicao, IconeInstituicao],
    ["Cadastro", CalendarMonthIcon],
    ["Documentos", DescriptionIcon],
    ["Ações", SettingsIcon],
  ];

  useEffect(() => {
    async function fetchProcesses() {
      try {
        const token = localStorage.getItem("token");

        const response = await fetch(api.listProcessTypes(), {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`);
        }

        const processesData = await response.json();

        if (!Array.isArray(processesData)) {
          throw new Error("Resposta inválida da API de processos.");
        }

        const sortedProcesses = processesData
          .sort(
            (processA, processB) =>
              new Date(processA.startdate) - new Date(processB.startdate),
          );

        setProcesses(
          sortedProcesses.map((process) => ({
            ...process,
            formattedSeiNumber: formatSeiNumber(process.seinumber),
          })),
        );
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchProcesses();
  }, []);

  const handleCardClick = (process, situacao) => {
    const destino = `/detalhes-processo/${process.id}/${process.schoolname}`;

    const parametros = situacao ? `?${new URLSearchParams({ situacao })}` : "";

    navigate(`${destino}${parametros}`);
  };

  const termoNormalizado = searchTerm.trim().toLocaleLowerCase("pt-BR");

  const filteredProcesses = processes.filter(
    (process) =>
      process.tipoInstituicao === abaAtiva &&
      [
        process.processtype,
        process.schoolname,
        process.seinumber,
        process.formattedSeiNumber,
      ].some((valor) =>
        String(valor ?? "")
          .toLocaleLowerCase("pt-BR")
          .includes(termoNormalizado),
      ),
  );

  const handleChangePage = (event, value) => {
    setPaginas((anterior) => ({
      ...anterior,
      [abaAtiva]: value,
    }));

    listaRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const totalProcesses = filteredProcesses.length;

  const totalPages = Math.ceil(totalProcesses / ITEMS_PER_PAGE);

  const primeiroProcesso =
    totalProcesses === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;

  const ultimoProcesso = Math.min(page * ITEMS_PER_PAGE, totalProcesses);

  const paginatedProcesses = filteredProcesses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  return (
    <TransicaoPagina variante="navegacao" data-pagina-rolavel="">
      {/* CABEÇALHO */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
            color: "var(--sear-azul, #087cf0)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <FolderCopyIcon sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              color: coresTextosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            Processos
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              color: "var(--sear-texto-secundario, #7b8798)",
              fontSize: tamanhosControlesGlobais.descricao,
            }}
          >
            Consulte e acompanhe os processos das escolas e das mantenedoras.
          </Typography>
        </Box>
      </Box>

      {/* ABAS ESCOLAS E MANTENEDORAS */}
      <Box
        role="tablist"
        aria-label="Processos por tipo de instituição"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          mb: 1.25,
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "var(--sear-superficie-azul, #f7fbff)",
        }}
      >
        {[
          {
            valor: "escola",
            nome: "Escolas",
            Icone: SchoolRoundedIcon,
          },
          {
            valor: "mantenedora",
            nome: "Mantenedoras",
            Icone: AccountBalanceRoundedIcon,
          },
        ].map(({ valor, nome, Icone }) => {
          const selecionada = abaAtiva === valor;

          return (
            <ButtonBase
              key={valor}
              role="tab"
              aria-selected={selecionada}
              aria-controls="lista-processos-escola"
              onClick={() => {
                setAbaAtiva(valor);
                listaRef.current?.scrollTo({
                  top: 0,
                });
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.8,
                minWidth: 0,
                minHeight: 47,
                px: 1,
                py: 1,
                borderBottom: "3px solid",
                borderColor: selecionada
                  ? "var(--sear-azul, #087cf0)"
                  : "transparent",
                bgcolor: selecionada
                  ? "var(--sear-superficie-azul, #edf6fe)"
                  : "transparent",
                color: selecionada
                  ? "var(--sear-azul, #087cf0)"
                  : "var(--sear-texto-secundario, #71829d)",
                "&.Mui-focusVisible": {
                  outline: "2px solid var(--sear-azul, #087cf0)",
                  outlineOffset: -2,
                },
              }}
            >
              <Icone
                sx={{
                  fontSize: 19,
                  flexShrink: 0,
                }}
              />

              <Typography
                component="span"
                sx={{
                  fontSize: tamanhosControlesGlobais.botao,
                  fontWeight: 700,
                }}
              >
                {nome}
              </Typography>

              <Chip
                size="small"
                label={`${quantidades[valor]} ${
                  quantidades[valor] === 1 ? "processo" : "processos"
                }`}
                sx={{
                  display: {
                    xs: "none",
                    sm: "inline-flex",
                  },
                  height: 20,
                  maxWidth: "100%",
                  bgcolor: selecionada
                    ? "var(--sear-azul, #087cf0)"
                    : "var(--sear-superficie-azul, #e7f1fc)",
                  color: selecionada
                    ? "#fff"
                    : "var(--sear-texto-secundario, #71829d)",
                  fontSize: tamanhosControlesGlobais.contador,
                  fontWeight: 700,
                  "& .MuiChip-label": {
                    px: {
                      xs: 0.5,
                      sm: 1,
                    },
                  },
                }}
              />
            </ButtonBase>
          );
        })}
      </Box>

      {/* PESQUISA INDEPENDENTE POR ABA */}
      <Box
        component="section"
        aria-label="Pesquisa de processos"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 1.5,
          py: 0.65,
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 2,
          bgcolor: "var(--sear-superficie-azul, #f7fbff)",
        }}
      >
        <TextField
          size="small"
          placeholder={`Pesquisar por ${
            tipoEscola ? "escola" : "mantenedora"
          }, processo ou SEI...`}
          value={searchTerm}
          onChange={(event) => {
            const valor = event.target.value;

            setBuscas((anterior) => ({
              ...anterior,
              [abaAtiva]: valor,
            }));

            setPaginas((anterior) => ({
              ...anterior,
              [abaAtiva]: 1,
            }));

            listaRef.current?.scrollTo({
              top: 0,
            });
          }}
          inputProps={{
            "aria-label": `Pesquisar processos de ${
              tipoEscola ? "escolas" : "mantenedoras"
            }`,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    fontSize: 18,
                    color: "var(--sear-azul, #087cf0)",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            width: {
              xs: "100%",
              md: 460,
            },
            minWidth: 0,
            "& .MuiOutlinedInput-root": {
              bgcolor: "var(--sear-superficie, #fff)",
              borderRadius: 1,
              minHeight: 34,
              fontSize: tamanhosControlesGlobais.campoBusca,
              "& fieldset": {
                borderColor: "var(--sear-borda, #c9def4)",
              },
              "& input": {
                py: 0.8,
              },
            },
          }}
        />

        <Typography
          sx={{
            ...ESTILO_TEXTO_TABELA,
            color: "var(--sear-texto-secundario, #7b8798)",
            whiteSpace: "nowrap",
            display: {
              xs: "none",
              md: "block",
            },
          }}
        >
          {ITEMS_PER_PAGE} processos por página
        </Typography>
      </Box>

      {/* LISTAGEM */}
      <Box
        ref={listaRef}
        id="lista-processos-escola"
        role="tabpanel"
        aria-label={`Processos das ${tipoEscola ? "Escolas" : "Mantenedoras"}`}
        data-rolagem-conteudo=""
        sx={{
          mt: 1.5,
          flex: {
            md: "1 1 0",
          },
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            border: "1px solid var(--sear-borda, #d7e7f7)",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "var(--sear-superficie, #fff)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.75,
              bgcolor: "var(--sear-superficie-azul, #f7fbff)",
              borderBottom: "1px solid var(--sear-borda, #d7e7f7)",
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: tamanhosPaginasAdministrativas.tituloSecao,
                color: coresTextosGlobais.tituloSecao,
                fontWeight: 700,
              }}
            >
              Processos das {tipoEscola ? "Escolas" : "Mantenedoras"}
            </Typography>

            <Chip
              size="small"
              label={totalProcesses}
              sx={{
                height: 21,
                bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosControlesGlobais.contador,
                fontWeight: 700,
              }}
            />
          </Box>

          {loading ? (
            <Box
              sx={{
                p: 5,
                textAlign: "center",
              }}
            >
              <CircularProgress size={32} />

              <Typography
                sx={{
                  ...ESTILO_TEXTO_TABELA,
                  mt: 2,
                }}
              >
                Carregando processos...
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                p: 3,
                color: "error.main",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                }}
              >
                Ocorreu um erro
              </Typography>

              <Typography sx={{ mt: 1 }}>{error}</Typography>
            </Box>
          ) : !paginatedProcesses.length ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography sx={ESTILO_TEXTO_TABELA}>
                Nenhum processo encontrado
              </Typography>

              <Typography
                sx={{
                  ...ESTILO_TEXTO_TABELA,
                  mt: 1,
                }}
              >
                Tente ajustar sua pesquisa.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
              }}
            >
              <Box
                role="table"
                aria-label={`Processos das ${
                  tipoEscola ? "Escolas" : "Mantenedoras"
                }`}
                sx={{
                  minWidth: 980,
                }}
              >
                <Box
                  role="row"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_PROCESSOS,
                    bgcolor: "var(--sear-superficie-azul, #edf6fe)",
                    minHeight: 34,
                    borderBottom: "1px solid var(--sear-borda, #d7e7f7)",
                  }}
                >
                  {colunasProcessos.map(([label, Icone]) => (
                    <Box
                      role="columnheader"
                      key={label}
                      sx={{
                        ...ESTILO_CELULA,
                        gap: 0.7,
                        py: 0.55,
                        color: "var(--sear-azul, #27456f)",
                        justifyContent: [
                          "Cadastro",
                          "Documentos",
                          "Ações",
                        ].includes(label)
                          ? "center"
                          : "flex-start",
                        "&:last-child": {
                          borderRight: 0,
                        },
                      }}
                    >
                      <Icone
                        sx={{
                          fontSize: 18,
                          color: "var(--sear-azul, #087cf0)",
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize:
                            tamanhosPaginasAdministrativas.cabecalhoTabela,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {paginatedProcesses.map((process) => (
                  <ProcessRow
                    key={process.id}
                    process={process}
                    IconeInstituicao={IconeInstituicao}
                    onClick={() => handleCardClick(process)}
                    onSelecionarSituacao={(situacao) =>
                      handleCardClick(process, situacao)
                    }
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {!loading && !error && totalProcesses > 0 && (
          <Box
            sx={{
              px: 1,
              py: 1,
            }}
          >
            <ControlesPaginacao
              page={page}
              totalPages={totalPages}
              totalProcesses={totalProcesses}
              primeiroProcesso={primeiroProcesso}
              ultimoProcesso={ultimoProcesso}
              onChange={handleChangePage}
            />
          </Box>
        )}
      </Box>
    </TransicaoPagina>
  );
}
